import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { MessageTemplatesService, type MessageTemplate, type TemplateCategory, type CreateTemplateInput, type GetTemplatesOptions } from '@/data/messageTemplates.service';

/**
 * Hook to get message templates.
 * For Keep Warm: useMessageTemplates(undefined, { scopeType: 'keep_warm' }) for keep_warm + global.
 */
export function useMessageTemplates(category?: TemplateCategory, options?: GetTemplatesOptions) {
  const { activeWorkspaceId } = useWorkspaceScope();

  return useQuery({
    queryKey: ['message-templates', activeWorkspaceId, category, options?.scopeType],
    queryFn: async (): Promise<MessageTemplate[]> => {
      if (!activeWorkspaceId) throw new Error('No workspace');
      const opts = options ?? (category ? { category } : undefined);
      return MessageTemplatesService.getTemplates(activeWorkspaceId, opts);
    },
    enabled: !!activeWorkspaceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to create template
 */
export function useCreateTemplate() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceScope();

  return useMutation({
    mutationFn: async (input: Omit<CreateTemplateInput, 'workspace_id'>) => {
      if (!activeWorkspaceId) throw new Error('No workspace');
      return MessageTemplatesService.createTemplate({
        ...input,
        workspace_id: activeWorkspaceId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['message-templates'], exact: false });
    },
  });
}

/**
 * Hook to update template
 */
export function useUpdateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<CreateTemplateInput> }) => {
      return MessageTemplatesService.updateTemplate(id, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['message-templates'], exact: false });
    },
  });
}

/**
 * Hook to delete template
 */
export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateId: string) => {
      return MessageTemplatesService.deleteTemplate(templateId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['message-templates'], exact: false });
    },
  });
}

